/**
 * The vocabulary of a field's value, shared by every review surface.
 *
 * A checkbox or a signature is stored as text like everything else — the
 * worker writes whatever its model produced ("true", "1", "x", "angekreuzt",
 * or nothing at all) — so reading one back is an interpretation, not a cast.
 * It lives here rather than in a screen because the two review screens, the
 * editor and the queue all have to agree on what "angekreuzt" means, and two
 * tables of synonyms would drift.
 *
 * Ported from the Flutter app's `utils/checkbox_result.dart`, which is the
 * table that has actually met production data.
 */

/** The three shapes a field can be reviewed in. */
export type ReviewFieldType = 'text' | 'checkbox' | 'signature'

/**
 * A binary field's reading. `unknown` is a real answer and the common one on
 * a bad scan: the recognizer wrote nothing, or wrote something neither list
 * claims.
 */
export type BinaryState = 'yes' | 'no' | 'unknown'

/** What a correction writes. The worker and the exports read these two. */
export const BINARY_TRUE = 'true'
export const BINARY_FALSE = 'false'

const YES = new Set([
  'true',
  '1',
  'x',
  'yes',
  'y',
  'ja',
  'checked',
  'angekreuzt',
  'gesetzt',
  'an',
  'signiert',
  'vorhanden',
  '✓',
  '✔',
])

const NO = new Set([
  'false',
  '0',
  'no',
  'n',
  'nein',
  'unchecked',
  'nicht angekreuzt',
  'nicht signiert',
  'leer',
  'aus',
  'fehlt',
])

/** Interprets a stored value as a binary state. Anything unrecognised is
 *  `unknown` rather than a guess — a checkbox read as "unsicher" has not
 *  been read. */
export function binaryState(value: string | undefined | null): BinaryState {
  const normalised = (value ?? '').trim().toLowerCase()
  if (normalised === '') return 'unknown'
  if (YES.has(normalised)) return 'yes'
  if (NO.has(normalised)) return 'no'
  return 'unknown'
}

/** The value a correction to `state` writes. `unknown` writes the empty
 *  string, which is how the gateway stores "a reviewer says nothing is marked
 *  here" — see `reviewSubmission`. */
export function binaryValue(state: BinaryState): string {
  if (state === 'yes') return BINARY_TRUE
  if (state === 'no') return BINARY_FALSE
  return ''
}

/** The German words for each state, per field type. Fixed vocabulary: these
 *  exact words appear in the design and must not be re-phrased per screen. */
export const BINARY_LABELS: Record<Exclude<ReviewFieldType, 'text'>, Record<BinaryState, string>> = {
  checkbox: {
    yes: 'Angekreuzt',
    no: 'Nicht angekreuzt',
    unknown: 'Undefiniert',
  },
  signature: {
    yes: 'Signiert',
    no: 'Nicht signiert',
    unknown: 'Undefiniert',
  },
}

/** How a field's current value reads, for a status line. */
export function valueLabel(type: ReviewFieldType, value: string): string {
  if (type === 'text') return value.trim() === '' ? 'Kein Wert erkannt' : `„${value}“`
  return BINARY_LABELS[type][binaryState(value)]
}
